import { useState, useEffect } from "react";
import { X, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Newsletter signup modal — replaces the legacy Substack link. Captures
 * name + city + email + selected interest categories (tours / special
 * events / releases / personal newsletter) directly into the admin
 * console. When Resend Audiences is configured, this same modal will
 * dual-write into the Audience tagged with the chosen interests.
 */
const DEFAULT_OPTIONS = [
  { key: "tours",          label: "Tours & live shows" },
  { key: "special_events", label: "Special events" },
  { key: "releases",       label: "New releases" },
  { key: "newsletter",     label: "Personal newsletter" },
];

export default function NewsletterSignupModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  // Default all interests checked — most people want everything, easier to
  // uncheck than to discover the boxes exist.
  const [interests, setInterests] = useState(
    () => new Set(DEFAULT_OPTIONS.map((o) => o.key)),
  );
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setName(""); setEmail(""); setCity("");
        setInterests(new Set(options.map((o) => o.key)));
        setDone(false); setError(null); setSubmitting(false);
      }, 220);
      return () => clearTimeout(t);
    }
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open, options]);

  useEffect(() => {
    // Hydrate options from backend so the list stays in lockstep with
    // whatever categories George adds server-side.
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/newsletter/options");
        if (alive && Array.isArray(data?.options) && data.options.length) {
          setOptions(data.options);
          setInterests(new Set(data.options.map((o) => o.key)));
        }
      } catch {
        // Fall back to defaults; not fatal.
      }
    })();
    return () => { alive = false; };
  }, []);

  const toggle = (key) => {
    setInterests((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/newsletter/signup", {
        name: name.trim(),
        email: email.trim(),
        city: city.trim(),
        interests: Array.from(interests),
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-5 py-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="newsletter-modal"
    >
      <div
        className="absolute inset-0 bg-black/82 backdrop-blur-md"
        onClick={onClose}
        data-testid="newsletter-modal-backdrop"
      />
      <div
        className="relative w-full max-w-md border border-white/15 px-6 py-7 sm:px-8 sm:py-9 my-auto"
        style={{ background: "rgba(8,11,22,0.96)" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-white/55 hover:text-white transition w-8 h-8 flex items-center justify-center"
          aria-label="Close"
          data-testid="newsletter-modal-close"
        >
          <X className="w-4 h-4" strokeWidth={1.4} />
        </button>

        {!done ? (
          <>
            <h2
              className="text-center mb-2 text-2xl sm:text-3xl"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
            >
              Mailing list
            </h2>
            <p className="text-center text-white/65 text-[12px] leading-relaxed mb-6">
              Add your details to hear about future shows, releases and events.
            </p>

            <form onSubmit={onSubmit} className="flex flex-col gap-3" data-testid="newsletter-form">
              <input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-[13px] text-white placeholder-white/35"
                data-testid="newsletter-name"
              />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-[13px] text-white placeholder-white/35"
                data-testid="newsletter-email"
              />
              <input
                type="text"
                placeholder="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-[13px] text-white placeholder-white/35"
                data-testid="newsletter-city"
              />

              <fieldset className="mt-3 border border-white/15 px-4 py-3.5" data-testid="newsletter-interests">
                <legend className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/65 px-1.5">
                  Notify me about
                </legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {options.map((o) => {
                    const checked = interests.has(o.key);
                    return (
                      <label
                        key={o.key}
                        className="flex items-center gap-2.5 cursor-pointer group select-none"
                        data-testid={`newsletter-interest-${o.key}-label`}
                      >
                        <span
                          className={`w-4 h-4 border flex items-center justify-center transition ${
                            checked
                              ? "border-[var(--mm-accent)] bg-[var(--mm-accent)]/15"
                              : "border-white/30 group-hover:border-white/55"
                          }`}
                        >
                          {checked && <Check className="w-3 h-3" strokeWidth={2.4} style={{ color: "var(--mm-accent)" }} />}
                        </span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(o.key)}
                          className="sr-only"
                          data-testid={`newsletter-interest-${o.key}`}
                        />
                        <span className="text-[12px] text-white/85 group-hover:text-white">
                          {o.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <button
                type="submit"
                disabled={submitting}
                className="btn-line w-full justify-center text-sm py-3 mt-2 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="newsletter-submit"
              >
                {submitting ? "Adding you…" : "Submit"}
              </button>

              {error && (
                <p className="font-mono text-[11px] text-rose-300/80 text-center" data-testid="newsletter-error">
                  {error}
                </p>
              )}
            </form>
          </>
        ) : (
          <div className="text-center py-3" data-testid="newsletter-success">
            <h2
              className="text-2xl sm:text-3xl mb-3"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
            >
              Thank you
            </h2>
            <p className="text-white/75 text-[13px] leading-relaxed mb-6">
              You&rsquo;ve been added to the list.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/70 hover:text-white underline underline-offset-4"
              data-testid="newsletter-close-done"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
